import { useCallback, useEffect, useState } from 'react';
import { leadsApi } from '@/lib/api/leads';
import { loadConfirmation } from './confirmationStorage';
import { clearReturningLead, loadReturningLead } from './returningLeadStorage';

/** What the server remembers of the quote this browser submitted earlier (masked, no contact details). */
export interface ReturningLead {
  firstName: string;
  maskedEmail: string;
  submittedAt?: string;
  quoteEmailStatus?: string;
  portalAvailable: boolean;
}

export type ReturningLeadState =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'converted' }
  | { status: 'returning'; lead: ReturningLead };

/**
 * Recognises a visitor who already submitted the quote (same browser, days later) so the landing can send
 * them to their next steps instead of an empty wizard. Submitted beats draft: the saved draft is dropped.
 * `loading` lasts one small request, so callers keep their CTA neutral meanwhile instead of flashing it.
 */
export function useReturningLead() {
  const [state, setState] = useState<ReturningLeadState>(() => {
    // A confirmation from this visit already covers it; no need to ask the server
    if (!loadReturningLead() || loadConfirmation()) return { status: 'none' };
    return { status: 'loading' };
  });

  useEffect(() => {
    if (state.status !== 'loading') return;
    const saved = loadReturningLead();
    if (!saved) {
      setState({ status: 'none' });
      return;
    }

    let cancelled = false;
    leadsApi
      .returnStatus(saved.leadId, saved.returnToken)
      .then(({ data }) => {
        if (cancelled) return;
        const result = data.data;
        if (result.converted) {
          setState({ status: 'converted' });
        } else {
          setState({ status: 'returning', lead: result });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        // 403 = expired or invalid: forget it silently. Anything else (offline, 5xx) keeps it for next time.
        if (error?.response?.status === 403) clearReturningLead();
        setState({ status: 'none' });
      });

    return () => {
      cancelled = true;
    };
    // Runs once, for the initial 'loading' state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** "¿No eres tú?": forget this visitor's quote on this device and fall back to the normal landing. */
  const clear = useCallback(() => {
    clearReturningLead();
    setState({ status: 'none' });
  }, []);

  return { state, clear };
}
